import type { LanguageIsoCode } from '@/graphql/graphql';
import {
    addToCart as apiAddToCart,
    removeFromCart as apiRemoveFromCart,
    updateCart as apiUpdateCart,
    getCartSummary,
} from '@/lib/api/webapp/cart';
import { useCallback, useEffect, useState } from 'react';
import { CartContext } from './cart-context';
import { useUserData } from './user-data-context';

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [cartItemsCount, setCartItemsCount] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const { isBuyer, loading: userDataLoading } = useUserData();

    useEffect(() => {
        let cancelled = false;

        if (userDataLoading) {
            setIsReady(false);
            return;
        }

        if (!isBuyer) {
            setCartItemsCount(0);
            setIsReady(true);
            return;
        }

        setIsReady(false);

        (async () => {
            try {
                const summary = await getCartSummary();
                if (!cancelled) setCartItemsCount(summary.itemsCount);
            } catch {
                if (!cancelled) setCartItemsCount(0);
            } finally {
                if (!cancelled) setIsReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isBuyer, userDataLoading]);

    const refreshCartSummary = useCallback(async () => {
        try {
            const summary = await getCartSummary();
            setCartItemsCount(summary.itemsCount);
            setIsReady(true);
            return summary.itemsCount;
        } catch {
            setCartItemsCount(0);
            setIsReady(true);
            return 0;
        }
    }, []);

    const addToCart = async (programId: string, language: LanguageIsoCode, excludeEpisodeIds?: string[]) => {
        const updatedCart = await apiAddToCart(programId, language, excludeEpisodeIds);
        setCartItemsCount(updatedCart.programs.length);
        setIsReady(true);
        return updatedCart;
    };

    const removeFromCart = async (programId: string, language: LanguageIsoCode) => {
        try {
            const updatedCart = await apiRemoveFromCart(programId, language);
            setCartItemsCount(updatedCart.programs.length);
            setIsReady(true);
            return updatedCart;
        } catch {
            return null;
        }
    };

    const updateCart = async (programId: string, currentLanguage: LanguageIsoCode, newLanguage: LanguageIsoCode) => {
        try {
            const updatedCart = await apiUpdateCart(programId, currentLanguage, newLanguage);
            setCartItemsCount(updatedCart.programs.length);
            setIsReady(true);
            return updatedCart;
        } catch {
            return null;
        }
    };

    return (
        <CartContext.Provider
            value={{ cartItemsCount, isReady, refreshCartSummary, addToCart, removeFromCart, updateCart }}
        >
            {children}
        </CartContext.Provider>
    );
};
